/**
 * MCPServerList - Component for displaying and managing MCP server configurations
 *
 * Features:
 * - Display list of configured MCP servers
 * - Health status indicators (connected/disconnected/error/unknown)
 * - Add, Edit, Delete server actions
 * - Refresh tools functionality
 * - Empty state when no servers configured
 * - Loading and error states
 * - Built-in email server with credential management (AC-1108)
 *
 * Technical Design: /workspace/docs/technical/TDD-MCP-Integration.md Section 3.2
 * Ticket: AC-778, AC-1108
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Server,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle,
  Loader,
} from 'lucide-react';
import apiService from '../../services/api';
import { MCPServer, MCPHealthStatus, MCPServerTemplate, BuiltinCredentialStatus } from '../../types';
import MCPServerConfig from './MCPServerConfig';
import MCPServerTemplates from './MCPServerTemplates';
import EmailSetupWizard from './EmailSetupWizard';
import Tooltip from '../common/Tooltip';

interface MCPServerListProps {
  projectId: number;
}

/**
 * Health status badge component
 */
const HealthStatusBadge: React.FC<{ status: MCPHealthStatus; error?: string }> = ({
  status,
  error,
}) => {
  const getStatusConfig = (status: MCPHealthStatus) => {
    switch (status) {
      case 'connected':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'Connected',
          className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        };
      case 'disconnected':
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: 'Disconnected',
          className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          label: 'Error',
          className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        };
      case 'unknown':
      default:
        return {
          icon: <HelpCircle className="w-4 h-4" />,
          label: 'Unknown',
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className="flex flex-col">
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
      {error && status === 'error' && (
        <span className="text-xs text-red-600 mt-1">{error}</span>
      )}
    </div>
  );
};

const MCPServerList: React.FC<MCPServerListProps> = ({ projectId }) => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [builtinServers, setBuiltinServers] = useState<MCPServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MCPServerTemplate | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [deleteConfirmServer, setDeleteConfirmServer] = useState<MCPServer | null>(null);
  const [refreshingServerId, setRefreshingServerId] = useState<number | null>(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Email setup wizard state (AC-1108)
  const [showEmailWizard, setShowEmailWizard] = useState(false);
  const [emailWizardServerId, setEmailWizardServerId] = useState<number | null>(null);
  const [emailWizardCredentialId, setEmailWizardCredentialId] = useState<number | null>(null);
  const [emailCredentialStatuses, setEmailCredentialStatuses] = useState<Record<number, BuiltinCredentialStatus[]>>({});

  // Email credential delete confirmation state
  const [emailDeleteConfirm, setEmailDeleteConfirm] = useState<{
    serverId: number;
    credentialId: number;
    label: string;
    dependentAgents: Array<{ agent_id: number; agent_name: string }>;
  } | null>(null);

  /**
   * Load MCP servers from API
   */
  const loadServers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.listMCPServers(projectId);
      // Separate built-in servers from user servers
      const builtin = data.filter(s => s.scope === 'built_in');
      const userServers = data.filter(s => s.scope !== 'built_in');
      setBuiltinServers(builtin);
      setServers(userServers);
      setHasInitialLoad(true);
    } catch (err: any) {
      setError(err.message || 'Error loading MCP servers');
      setHasInitialLoad(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Built-in server IDs that require per-user credential setup
  const CREDENTIAL_REQUIRED_SERVER_IDS = new Set([-2]); // -2 = Email; -1 = Browser (no setup needed)

  /**
   * Load credential status for built-in servers that need credentials (AC-1108)
   */
  const loadCredentialStatus = useCallback(async () => {
    const freshStatuses: Record<number, BuiltinCredentialStatus[]> = {};
    for (const server of builtinServers) {
      // Only check credential status for servers that require credentials (e.g., Email -2)
      if (CREDENTIAL_REQUIRED_SERVER_IDS.has(server.mcp_server_id)) {
        try {
          freshStatuses[server.mcp_server_id] = await apiService.listBuiltinCredentials(server.mcp_server_id);
        } catch {
          freshStatuses[server.mcp_server_id] = [];
        }
      }
    }
    setEmailCredentialStatuses(freshStatuses);
  }, [builtinServers]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  useEffect(() => {
    if (builtinServers.length > 0) {
      loadCredentialStatus();
    }
  }, [builtinServers, loadCredentialStatus]);

  /**
   * Handle opening templates panel or config modal for new server
   */
  const handleAddServer = () => {
    setEditingServer(null);
    setSelectedTemplate(null);
    setShowTemplates(true);
  };

  /**
   * Handle selecting a template
   */
  const handleSelectTemplate = (template: MCPServerTemplate) => {
    setSelectedTemplate(template);
    setShowTemplates(false);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle creating from scratch (no template)
   */
  const handleCreateFromScratch = () => {
    setSelectedTemplate(null);
    setShowTemplates(false);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle opening config modal for editing existing server
   */
  const handleEditServer = (server: MCPServer) => {
    setEditingServer(server);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle closing config modal
   */
  const handleCloseModal = () => {
    setIsConfigModalOpen(false);
    setEditingServer(null);
    setSelectedTemplate(null);
  };

  /**
   * Handle closing templates panel
   */
  const handleCloseTemplates = () => {
    setShowTemplates(false);
  };

  /**
   * Handle successful save from config modal
   */
  const handleSaveServer = () => {
    loadServers();
  };

  /**
   * Handle delete server - first show confirmation
   */
  const handleDeleteClick = (server: MCPServer) => {
    setDeleteConfirmServer(server);
  };

  /**
   * Confirm delete and execute
   */
  const handleConfirmDelete = async () => {
    if (!deleteConfirmServer) return;

    try {
      await apiService.deleteMCPServer(projectId, deleteConfirmServer.mcp_server_id);
      setDeleteConfirmServer(null);
      loadServers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete server');
      setDeleteConfirmServer(null);
    }
  };

  /**
   * Cancel delete confirmation
   */
  const handleCancelDelete = () => {
    setDeleteConfirmServer(null);
  };

  /**
   * Handle refresh tools for a server
   */
  const handleRefreshTools = async (server: MCPServer) => {
    try {
      setRefreshingServerId(server.mcp_server_id);
      await apiService.discoverMCPTools(projectId, server.mcp_server_id);
      loadServers();
    } catch (err: any) {
      setError(err.message || 'Failed to refresh tools');
    } finally {
      setRefreshingServerId(null);
    }
  };

  /**
   * Handle opening email setup wizard (AC-1108)
   */
  const handleOpenEmailWizard = (serverId: number, credentialId?: number) => {
    setEmailWizardServerId(serverId);
    setEmailWizardCredentialId(credentialId ?? null);
    setShowEmailWizard(true);
  };

  /**
   * Handle email wizard close
   */
  const handleCloseEmailWizard = () => {
    setShowEmailWizard(false);
    setEmailWizardServerId(null);
    setEmailWizardCredentialId(null);
  };

  /**
   * Handle email wizard save complete
   */
  const handleEmailWizardComplete = () => {
    setShowEmailWizard(false);
    setEmailWizardServerId(null);
    setEmailWizardCredentialId(null);
    // Refresh credential status
    loadCredentialStatus();
  };

  /**
   * Handle deleting an email credential instance — checks for dependent agents first
   */
  const handleDeleteEmailCredential = async (serverId: number, credentialId: number, label: string) => {
    try {
      const { dependent_agents } = await apiService.getCredentialDependents(serverId, credentialId);
      setEmailDeleteConfirm({ serverId, credentialId, label, dependentAgents: dependent_agents });
    } catch (err: any) {
      setError(err.message || 'Failed to check credential dependencies');
    }
  };

  const handleConfirmEmailDelete = async () => {
    if (!emailDeleteConfirm) return;
    try {
      await apiService.deleteBuiltinCredentials(emailDeleteConfirm.serverId, emailDeleteConfirm.credentialId);
      setEmailDeleteConfirm(null);
      loadCredentialStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to delete email credentials');
      setEmailDeleteConfirm(null);
    }
  };

  /**
   * Render a built-in server row (AC-1108, AC-1116)
   * Distinguishes between servers that need credentials (Email) and those that don't (Browser Automation).
   */
  const renderBuiltinServerRow = (server: MCPServer) => {
    const needsCredentials = CREDENTIAL_REQUIRED_SERVER_IDS.has(server.mcp_server_id);
    const credStatuses = emailCredentialStatuses[server.mcp_server_id] || [];
    const hasCredentials = credStatuses.length > 0;
    const ServerIcon = needsCredentials ? Mail : Server;

    return (
      <React.Fragment key={server.mcp_server_id}>
        {/* Server header row */}
        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900" data-tutorial={needsCredentials ? 'email-server-row' : undefined}>
          <td className="px-4 py-4">
            <div className="flex items-center gap-3">
              <ServerIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-warm-white">{server.name}</span>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                    data-testid="builtin-badge"
                  >
                    Built-in
                  </span>
                </div>
                {server.description && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">{server.description}</div>
                )}
              </div>
            </div>
          </td>
          <td className="px-4 py-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              {server.transport_type}
            </span>
          </td>
          <td className="px-4 py-4">
            {needsCredentials ? (
              hasCredentials ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  <CheckCircle className="w-4 h-4" />
                  {credStatuses.length} account{credStatuses.length !== 1 ? 's' : ''} configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                  <AlertCircle className="w-4 h-4" />
                  Setup Required
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                <CheckCircle className="w-4 h-4" />
                Available
              </span>
            )}
          </td>
          <td className="px-4 py-4 text-right">
            {needsCredentials ? (
              <button
                onClick={() => handleOpenEmailWizard(server.mcp_server_id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
                data-tutorial="btn-add-email-account"
              >
                <Plus className="w-4 h-4" />
                Add Email Account
              </button>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">No setup needed</span>
            )}
          </td>
        </tr>
        {/* Credential sub-rows */}
        {needsCredentials && credStatuses.map((cred) => (
          <tr key={cred.credential_id} className="bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800">
            <td className="px-4 py-3 pl-14">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <div>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {cred.instance_label || 'Email'}
                  </span>
                  {cred.email && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{cred.email}</span>
                  )}
                </div>
              </div>
            </td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                {cred.provider || 'custom'}
              </span>
            </td>
            <td className="px-4 py-3">
              {cred.last_test_success === true ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  <CheckCircle className="w-3 h-3" />
                  Connected
                </span>
              ) : cred.last_test_success === false ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                  <XCircle className="w-3 h-3" />
                  Failed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                  <HelpCircle className="w-3 h-3" />
                  Not tested
                </span>
              )}
            </td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => handleOpenEmailWizard(server.mcp_server_id, cred.credential_id!)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <Edit2 className="w-3 h-3" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteEmailCredential(server.mcp_server_id, cred.credential_id!, cred.instance_label || 'Email')}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/40"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  // All servers (built-in + user) for counting
  const allServers = [...builtinServers, ...servers];

  // Loading state - show spinner when loading initially
  if (isLoading && allServers.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader data-testid="loader-icon" className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error && allServers.length === 0) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading MCP servers: {error}</span>
        </div>
      </div>
    );
  }

  // Empty state - show templates directly (only when no servers at all including built-in)
  if (!isLoading && allServers.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header consistent with non-empty state for E2E test compatibility */}
        <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">MCP Servers</h3>

        <div className="text-center py-4">
          <Server className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No MCP servers configured</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Choose a template below to get started quickly, or configure manually.
          </p>
        </div>

        {/* Show templates inline when empty */}
        <MCPServerTemplates onSelectTemplate={handleSelectTemplate} />

        {/* Manual Configuration Option */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Or configure a custom MCP server manually:
          </p>
          <button
            onClick={handleCreateFromScratch}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <Server className="w-4 h-4" />
            Configure Manually
          </button>
        </div>

        {/* Config Modal for empty state */}
        {isConfigModalOpen && (
          <MCPServerConfig
            projectId={projectId}
            server={editingServer || undefined}
            template={selectedTemplate || undefined}
            onClose={handleCloseModal}
            onSave={handleSaveServer}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Server button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">MCP Servers</h3>
        <button
          onClick={handleAddServer}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <Plus data-testid="plus-icon" className="w-4 h-4" />
          Add Server
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-800 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Server list */}
      <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Server
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Transport
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {/* Built-in servers (AC-1108) */}
            {builtinServers.map((server) => renderBuiltinServerRow(server))}

            {/* User-configured servers */}
            {servers.map((server) => (
              <tr key={server.mcp_server_id} className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <Server data-testid="server-icon" className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-warm-white">{server.name}</div>
                      {server.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{server.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    {server.transport_type}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <HealthStatusBadge
                    status={server.health_status}
                    error={server.last_error || undefined}
                  />
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content="Refresh Tools">
                      <button
                        onClick={() => handleRefreshTools(server)}
                        disabled={refreshingServerId === server.mcp_server_id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                        aria-label="Refresh Tools"
                      >
                        <RefreshCw
                          data-testid="refresh-icon"
                          className={`w-4 h-4 ${
                            refreshingServerId === server.mcp_server_id ? 'animate-spin' : ''
                          }`}
                        />
                      </button>
                    </Tooltip>
                    <Tooltip content="Edit">
                      <button
                        onClick={() => handleEditServer(server)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                        aria-label="Edit"
                      >
                        <Edit2 data-testid="edit-icon" className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete">
                      <button
                        onClick={() => handleDeleteClick(server)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        aria-label="Delete"
                      >
                        <Trash2 data-testid="trash-icon" className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmServer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">Delete MCP Server</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete &quot;{deleteConfirmServer.name}&quot;? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
                aria-label="Confirm Delete"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Credential Delete Confirmation Dialog (AC-1128) */}
      {emailDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">
              Delete Email Account
            </h3>

            {emailDeleteConfirm.dependentAgents.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                  The following agents use this email account and will stop working:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {emailDeleteConfirm.dependentAgents.map((agent) => (
                    <li key={agent.agent_id} className="text-sm text-amber-700 dark:text-amber-400">
                      {agent.agent_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete &quot;{emailDeleteConfirm.label}&quot;? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEmailDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmEmailDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="templates-modal-title"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 id="templates-modal-title" className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                Add MCP Server
              </h2>
              <button
                onClick={handleCloseTemplates}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded"
                aria-label="Close"
              >
                <AlertCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <MCPServerTemplates onSelectTemplate={handleSelectTemplate} />

              {/* Manual Configuration Option */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Or configure a custom MCP server manually:
                </p>
                <button
                  onClick={handleCreateFromScratch}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <Server className="w-4 h-4" />
                  Configure Manually
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={handleCloseTemplates}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {isConfigModalOpen && (
        <MCPServerConfig
          projectId={projectId}
          server={editingServer || undefined}
          template={selectedTemplate || undefined}
          onClose={handleCloseModal}
          onSave={handleSaveServer}
        />
      )}

      {/* Email Setup Wizard Modal (AC-1108) */}
      {showEmailWizard && emailWizardServerId !== null && (
        <EmailSetupWizard
          builtinServerId={emailWizardServerId}
          credentialId={emailWizardCredentialId}
          onClose={handleCloseEmailWizard}
          onSaveComplete={handleEmailWizardComplete}
        />
      )}
    </div>
  );
};

export default MCPServerList;
